/**
 * OAuth Callback Handler
 * 
 * Handles the callback from first-party Google OAuth flow:
 * 1. Backend redirects here with token in URL fragment: /auth/callback#token=xxx&redirect=/path
 * 2. This component extracts the token
 * 3. Stores it in localStorage
 * 4. Redirects to the intended page
 * 
 * Also handles error cases with query string: /auth/callback?error=xxx
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function OAuthCallback() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [status, setStatus] = useState("processing");
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      // Check for errors in query string first
      const queryParams = new URLSearchParams(window.location.search);
      const queryError = queryParams.get('error');
      const queryDomain = queryParams.get('domain');

      if (queryError) {
        let userMessage = "Google sign-in failed";
        
        switch (queryError) {
          case 'GOOGLE_DOMAIN_NOT_ALLOWED':
          case 'domain_not_allowed':
            userMessage = queryDomain 
              ? `Access denied. The domain '${queryDomain}' is not allowed. Please sign in with your company Google Workspace account.`
              : "Access denied. This account is not allowed. Please sign in with your company Google Workspace account.";
            break;
          case 'account_deleted':
            userMessage = "This account has been deleted.";
            break;
          case 'oauth_not_configured':
            userMessage = "Google sign-in is not configured. Please contact your administrator.";
            break;
          case 'invalid_state':
            userMessage = "Security validation failed. Please try again.";
            break;
          case 'access_denied':
            userMessage = "Sign-in was cancelled.";
            break;
          case 'missing_code':
            userMessage = "Authentication failed. No authorization code received.";
            break;
          case 'token_exchange_failed':
          case 'token_exchange_error':
            userMessage = "Failed to complete sign-in. Please try again.";
            break;
          case 'redirect_uri_mismatch':
            userMessage = "Configuration error. Please contact your administrator.";
            break;
          default:
            userMessage = `Google sign-in error: ${queryError}`;
        }
        
        setStatus("error");
        setErrorMessage(userMessage);
        toast.error(userMessage, { duration: 6000 });
        
        // Redirect to login after delay
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
        return;
      }

      // Extract token from URL fragment
      const hash = window.location.hash;
      if (!hash) {
        // Check if this is a legacy relay scenario (popup flow)
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');
        
        if (sessionId && window.opener) {
          // Legacy popup flow - relay to opener
          window.opener.postMessage({ type: 'OAUTH_SUCCESS', session_id: sessionId }, window.location.origin);
          window.close();
          return;
        }
        
        // No token and no session_id - something went wrong
        setStatus("error");
        setErrorMessage("No authentication data received.");
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
        return;
      }

      const params = new URLSearchParams(hash.substring(1));
      const token = params.get('token');
      const redirect = params.get('redirect') || '/items';
      const error = params.get('error');

      if (error) {
        setStatus("error");
        setErrorMessage(`Sign-in error: ${error}`);
        toast.error(`Sign-in error: ${error}`);
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
        return;
      }

      if (!token) {
        setStatus("error");
        setErrorMessage("No token received.");
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
        return;
      }

      // Store token and complete login
      try {
        // Use the AuthContext loginWithToken function
        await loginWithToken(token);
        
        setStatus("success");
        toast.success("Signed in successfully!");
        
        // Navigate to intended destination
        navigate(redirect, { replace: true });
      } catch (err) {
        console.error("Login error:", err);
        setStatus("error");
        setErrorMessage("Failed to complete sign-in.");
        toast.error("Failed to complete sign-in.");
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
      }
    };

    processCallback();
  }, [navigate, loginWithToken]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center space-y-4">
        {status === "processing" && (
          <>
            <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-muted-foreground">Completing sign-in...</p>
          </>
        )}
        
        {status === "success" && (
          <>
            <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-muted-foreground">Signed in! Redirecting...</p>
          </>
        )}
        
        {status === "error" && (
          <>
            <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-400">{errorMessage}</p>
            <p className="text-muted-foreground text-sm">Redirecting to login...</p>
          </>
        )}
      </div>
    </div>
  );
}
